import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components';

export const TableContainer = styled.div<{ borderless?: boolean }>`
  background: var(--white);
  border: ${(props) =>
    props.borderless ? 'none' : '1px solid var(--grey-300)'};
  border-radius: 5px;
  display: flex;
  flex-direction: column;
  font-family: var(--raleway);
  gap: 0;
  padding-bottom: 3rem;
  padding-top: 0.6rem;
`;

export const TableHeader = styled.div`
  align-items: center;
  border-bottom: 1px solid var(--grey-300);
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-bottom: 1.1rem;
  padding: 0 1.2rem 0.6rem 0.9rem;
  row-gap: 0.6rem;
`;

export const TableTitle = styled.h2`
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0;
`;

export const SearchRow = styled.div`
  align-items: center;
  display: flex;
  /* Don't shrink relative to the title — wrap to the next line as a whole unit
     only when the two can't share one line. space-between on the header keeps it
     right of the title inline, and left-aligned under it when wrapped. */
  flex-shrink: 0;
  gap: 0.5rem;
  justify-content: flex-end;
  /* But never exceed its line: cap at full width so, when space runs out, the
     search input shrinks instead of pushing the icons out of bounds. */
  max-width: 100%;
  min-width: 0;
`;

export const TableIcon = styled(FontAwesomeIcon)<{ active?: boolean }>`
  color: ${(props) => (props.active ? 'var(--wbs-red)' : 'var(--grey-700)')};
  cursor: pointer;
  flex-shrink: 0;
  font-size: 1rem;
  transition: color 0.2s ease;

  &:hover {
    color: var(--wbs-red);
  }
`;

export const FilterIconWrapper = styled.div`
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  gap: 4px;
`;

export const FilterCount = styled.span<{ active?: boolean }>`
  color: ${(props) => (props.active ? 'var(--wbs-red)' : 'var(--grey-700)')};
  font-family: var(--open-sans);
  font-size: 12px;
  margin-top: 3px;
`;

export const Table = styled.table`
  border-collapse: collapse;
  table-layout: auto;
  width: 100%;
`;

export const TableBody = styled.tbody<{ borderless?: boolean }>`
  border: ${(props) =>
    props.borderless ? 'none' : '1px solid var(--grey-300)'};
  border-radius: 8px;
`;

export const TableRowGroup = styled.tbody<{ $borderless?: boolean }>`
  border-left: ${(props) =>
    props.$borderless ? 'none' : '1px solid var(--grey-300)'};
  border-right: ${(props) =>
    props.$borderless ? 'none' : '1px solid var(--grey-300)'};

  &:first-of-type {
    border-top: ${(props) =>
      props.$borderless ? 'none' : '1px solid var(--grey-300)'};
  }

  &:last-of-type {
    border-bottom: ${(props) =>
      props.$borderless ? 'none' : '1px solid var(--grey-300)'};
  }

  ${(props) =>
    !props.$borderless &&
    `
    &:hover tr {
      background-color: var(--grey-300);
    }
  `}
`;

export const Th = styled.th`
  font-size: 13px;
  font-weight: 600;
  padding: 0.5rem 0.35rem 0.7rem;
  text-align: left;
  text-transform: uppercase;

  &:not(:first-child) {
    padding-left: 0.45rem;
  }

  &:last-child {
    padding-right: 0.5rem;
  }
`;

export const Td = styled.td`
  font-family: var(--open-sans);
  font-size: 14px;
  padding: 0.7rem 0.3rem 0;
  vertical-align: top;

  &:first-child {
    padding-left: 0.5rem;
  }

  &:last-child {
    padding-right: 0.5rem;
  }
`;

export const TableRow = styled.tr<{ borderless?: boolean }>`
  border-bottom: ${(props) =>
    props.borderless ? 'none' : '1px solid var(--grey-300)'};
  transition: background-color 0.5s ease;
`;

export const EmptyTd = styled.td`
  color: var(--grey-900);
  font-family: var(--raleway);
  font-size: 14px;
  padding: 10px;
  text-align: center;
`;

export const ButtonTd = styled.td`
  border-bottom: 1px solid var(--grey-300);
  font-family: var(--open-sans);
  font-size: 0.875rem;
  padding: 1.1rem 0.5rem 0.6rem;
  text-align: right;
  vertical-align: top;
`;

export const ButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-end;
`;

export const SortableHeader = styled.div`
  align-items: center;
  color: var(--grey-800);
  cursor: pointer;
  display: flex;
  gap: 0.3rem;

  &:hover {
    color: var(--grey-600);
  }
`;

export const SortIcon = styled(FontAwesomeIcon)`
  color: var(--grey-500);
  font-size: 0.75rem;
`;

export const TableContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0 0.9rem;
`;

export const TableFooter = styled.div`
  color: var(--grey-800);
  display: flex;
  font-size: 13px;
  font-weight: 300;
  justify-content: space-between;
  margin: 3px 0 0 1px;
`;

export const SearchWrapper = styled.div`
  align-items: center;
  display: flex;
  /* Shrinkable: 190px when there's room, but gives up width to keep icons in view. */
  flex: 0 1 190px;
  min-width: 0;
  position: relative;
`;

export const SearchInput = styled.input`
  background: var(--white);
  border: 1px solid var(--grey-50);
  border-radius: 4px;
  box-shadow: var(--shadow-inset-md);
  color: var(--grey-600);
  font-family: var(--raleway);
  font-size: 13px;
  min-width: 0;
  padding: 6px 32px 6px 12px;

  transition:
    background 0.2s,
    border 0.2s;
  width: 100%;

  &::placeholder {
    color: var(--grey-600);
    font-family: var(--raleway);
    opacity: 1;
  }

  &:focus {
    background: var(--grey-50);
    border: 1px solid var(--grey-600);
    outline: none;
  }
`;

export const SearchIcon = styled(FontAwesomeIcon)`
  color: var(--grey-600);
  font-size: 1rem;
  pointer-events: none;
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
`;

export const SearchPlaceholder = styled.div`
  min-height: 0;
  padding: 14px 0;
  width: 220px;
`;

export const ActionGroup = styled.div`
  display: flex;
  gap: 8px;
`;

export const ScoreText = styled.div`
  white-space: pre-line;
`;

export const DesktopTableWrapper = styled.div<{ $mobileBreakpoint: number }>`
  @media (max-width: ${(props) => props.$mobileBreakpoint}px) {
    display: none;
  }
`;

export const MobileCardList = styled.div<{ $mobileBreakpoint: number }>`
  display: none;

  @media (max-width: ${(props) => props.$mobileBreakpoint}px) {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
`;

export const MobileCard = styled.div`
  background: var(--white);
  border: 1px solid var(--grey-300);
  border-radius: 6px;
  overflow: hidden;
  padding: 0 12px;
`;

export const MobileField = styled.div`
  align-items: baseline;
  display: flex;
  flex-direction: row;
  gap: 40px;
  padding: 12px 0;
  &:last-child {
    border-bottom: none;
  }
`;

export const MobileFieldLabel = styled.div`
  color: var(--grey-700);
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 700;
  margin-top: 2px;
  min-width: 90px;
  text-transform: uppercase;
`;

export const MobileFieldValue = styled.div`
  flex: 1;
  font-family: var(--open-sans);
  font-size: 13px;
  word-break: break-word;
`;

export const MobileActionsRow = styled.div`
  border-top: 1px solid var(--grey-200);
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-end;
  padding: 10px 0;
`;
